import type { FormInstance } from 'antd';
import type { NamePath } from 'antd/es/form/interface';

export function getChangedFieldPaths(
  changedValues: Record<string, unknown>,
  prefix: (string | number)[] = [],
): NamePath[] {
  const paths: NamePath[] = [];

  for (const [key, value] of Object.entries(changedValues)) {
    const path = [...prefix, /^\d+$/.test(key) ? Number(key) : key];

    if (Array.isArray(value)) {
      paths.push(path);
      value.forEach((item, index) => {
        if (item !== null && typeof item === 'object' && !(item instanceof Date)) {
          paths.push(...getChangedFieldPaths(item as Record<string, unknown>, [...path, index]));
        } else {
          paths.push([...path, index]);
        }
      });
      continue;
    }

    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      paths.push(...getChangedFieldPaths(value as Record<string, unknown>, path));
      continue;
    }

    paths.push(path);
  }

  return paths;
}

interface ErrorField {
  name: NamePath;
  errors: string[];
}

export function getFieldElementId(name: NamePath, formName?: string): string {
  const path = Array.isArray(name) ? name : [name];

  return [formName, ...path].filter(part => part !== undefined && part !== '').join('_');
}

function namePathFromElementId(elementId: string, formName?: string): NamePath {
  const pathId = formName && elementId.startsWith(`${formName}_`) ? elementId.slice(formName.length + 1) : elementId;

  return pathId.split('_').map(part => (/^\d+$/.test(part) ? Number(part) : part));
}

function resolveErrorFieldElement(name: NamePath, formName?: string): { element: HTMLElement; name: NamePath } | null {
  const exactId = getFieldElementId(name, formName);
  const exactElement = document.getElementById(exactId);

  if (exactElement) {
    return { element: exactElement, name };
  }

  // FormList / group validators often have no own DOM node; fall back to the first nested control.
  const nestedElement = document.querySelector<HTMLElement>(`[id^="${CSS.escape(`${exactId}_`)}"]`);
  if (!nestedElement?.id) {
    return null;
  }

  return { element: nestedElement, name: namePathFromElementId(nestedElement.id, formName) };
}

export function getTopmostErrorFieldName(errorFields: ErrorField[], formName?: string): NamePath | undefined {
  let topmostName: NamePath | undefined;
  let topmostOffset = Number.POSITIVE_INFINITY;

  for (const field of errorFields) {
    const resolved = resolveErrorFieldElement(field.name, formName);

    if (!resolved) {
      continue;
    }

    const { top } = resolved.element.getBoundingClientRect();
    if (top < topmostOffset) {
      topmostOffset = top;
      topmostName = resolved.name;
    }
  }

  // AntD reports errors in field registration order, which isn't necessarily visual order.
  return topmostName ?? errorFields[0]?.name;
}

/**
 * An `overflow: hidden` box counts as scrollable, and scrolling a field into view walks every
 * scrollable ancestor. The application form layout is one such box, holding the sticky header's
 * worth of hidden overflow, so a page-wide scroll slides the header's neighbours out of sight with
 * no scrollbar left to bring them back -- only a reload fixes it. Scrolling the field's own
 * container keeps the movement inside the form.
 */
function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);

    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
}

export function scrollToTopmostError(form: FormInstance, errorFields: ErrorField[], formName?: string): void {
  const name = getTopmostErrorFieldName(errorFields, formName);

  if (!name) {
    return;
  }

  const resolved = resolveErrorFieldElement(name, formName);
  const container = resolved && findScrollContainer(resolved.element);

  if (!resolved || !container) {
    // Nothing of ours to scroll, so let AntD do it -- 'if-needed' rather than 'always' so a field
    // that is already on screen can't shift the layout.
    form.scrollToField(name, { behavior: 'smooth', block: 'center', scrollMode: 'if-needed', focus: true });
    return;
  }

  const elementRect = resolved.element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const centreOffset = (container.clientHeight - elementRect.height) / 2;

  container.scrollTo({
    top: container.scrollTop + elementRect.top - containerRect.top - centreOffset,
    behavior: 'smooth',
  });
  // A plain focus() scrolls the ancestors this function exists to leave alone.
  resolved.element.focus({ preventScroll: true });
}

export function clearErrorsForChangedValues(form: FormInstance, changedValues: Record<string, unknown>): void {
  const paths = getChangedFieldPaths(changedValues);
  if (paths.length === 0) {
    return;
  }

  form.setFields(paths.map(name => ({ name, errors: [] })));
}

/**
 * The house form validation standard:
 *  - errors surface on submit, not while the user types
 *  - a failed submit scrolls the topmost invalid field into view and focuses it
 *  - editing a field clears its error immediately, until the next submit
 *
 * Spread into a form's props — `BtlBaseForm` reads both keys and switches on
 * `clearErrorsForChangedValues` and `scrollToTopmostError` accordingly.
 *
 * Not OO-specific. Every form in the portal should adopt this; pages do so incrementally.
 *
 * `as const` keeps `validateTrigger` a string literal so BtlBaseForm's
 * `validateTrigger === 'onSubmit'` comparison narrows correctly.
 */
export const standardFormValidationProps = {
  validateTrigger: 'onSubmit',
  scrollToFirstError: true,
} as const;
